/**
 * Generic Google Tag Manager data layer utilities
 */

/**
 * Pushes data to Google Tag Manager's dataLayer
 * @param data - The data object to push to dataLayer
 */
export const pushToDataLayer = (data: Record<string, any>) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

/**
 * Creates user-specific data for GTM
 * @param userId - User ID or null
 * @returns Data object to push to dataLayer
 */
export const createUserDataForGTM = (userId: string | null) => {
  return {
    user_id: userId,
  };
};

/**
 * Creates sign_up event data for GTM
 * @param signupMethod - The method used for signup/login
 * @returns Data object to push to dataLayer
 */
export const createSignUpEventForGTM = (signupMethod: string) => {
  return {
    event: "sign_up",
    signup_method: signupMethod,
  };
};

/**
 * Creates ecommerce event data for GTM
 * @param eventName - The ecommerce event name (e.g., "view_item", "add_to_cart", "purchase")
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @param finalPrice - The final price for the item
 * @param productId - Product ID
 * @param productName - Product name
 * @param skuId - The SKU ID for the specific variant
 * @returns Data object to push to dataLayer for ecommerce events
 */
export const createEcommerceEventForGTM = (
  eventName: string,
  currency: string,
  finalPriceInCent: number,
  productId: string,
  productName: string,
  skuId: string
) => {
  return {
    event: eventName,
    ecommerce: {
      currency,
      value: finalPriceInCent / 100,
      items: [
        {
          item_id: productId,
          item_name: productName,
          item_variant: skuId,
          price: finalPriceInCent / 100,
        },
      ],
    },
  };
};

/**
 * Creates start_trial event data for GTM
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @param trialDuration - Duration of trial in days as string (e.g., "1d", "2d", "30d") (optional)
 * @returns Data object to push to dataLayer for start_trial events
 */
export const createStartTrialEventForGTM = (currency: string, trialDuration?: string) => {
  const eventData: any = {
    event: "start_trial",
    subscription_product_id: "blidz_plus_trial",
    subscription_price: 0,
    subscription_currency: currency,
  };

  if (trialDuration !== undefined) {
    eventData.trial_duration = trialDuration;
  }

  return eventData;
};
